import React, { useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Grid, TextField, Autocomplete, Checkbox } from "@mui/material";
import { BASE_URL } from "./../../global";
import ExcelDownload from "../../utils/components/excelDownload";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import ListWrap from "../commonComponent/listWrap";
import moment from "moment";
import { getFormattedDates } from "../../utils/utils";
import DownloadPdfLoaderModal from "../../utils/components/downloadPdfLoader";

let notNeededColumn = ["id","calibratedby"];

const headers = [
  { key: "companyName", label: "Client Name" },
  { key: "certificateNumber", label: "Certificate Number" },
  { key: "instrumentName", label: "Instrument Name" },
  { key: "DUCID", label: "DUCID" },
  { key: "serialNumber", label: "Serial No" },
  { key: "calibrationDate", label: "Date of Calibration" },
  { key: "standardId", label: "Master Equipment Used (StdID)" },
];
export default function MasterReport() {
  const [page, setPage] = React.useState(0);
  const [searchSRNO, setsearchSRNO] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [statusData, setStatusData] = React.useState([]);
  const [selectedStdID, setSelectedStdID] = React.useState([]);
  const [from, setFrom] = React.useState(null);
  const [to, setTo] = React.useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const printComponentRef = React.useRef();
  const [searched, setSearched] = React.useState(false);
  const [allStandards, setAllStandards] = React.useState([]);
  const [downloadPdfloading, setDownloadPdfloading] = React.useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getData = () => {
    const url = BASE_URL;
    let whereConditions = "";

    if (to != null && from != null) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      if (whereConditions === "") {
        whereConditions = " where ";
      } else {
        whereConditions += " and ";
      }

      whereConditions += ` calibrationDate between '${fromDate}' and '${toDate}'`;
    }
    if (whereConditions == "") {
      whereConditions += ` where status != -1`;
    } else {
      whereConditions += ` and status != -1 `;
    }
    const query = {
      query: `select * from certificatesView ${whereConditions} order by id DESC`,
    };

    setDownloadPdfloading(true);

    axiosWithToken
      .post(url + `dynamic`, query)
      .then(async (res) => {
        // const certificates = certificates.map((item) => item.id);
        const allCertificates = res.data;
        const certificates = allCertificates.map((item) => {
          return {
            id: item.id,
            companyName: item.companyName,
            certificateNumber: item.certificateNumber,
            instrumentName: item.instrumentName,
            DUCID: item.DUCID,
            serialNumber: item.serialNumber,
            calibrationDate: item.calibrationDate
              ? moment(item.calibrationDate).format("DD/MM/YYYY")
              : "",
            calibratedby: item.calibratedby,
          };
        });

        // const datasheetIds = certificates.map((item) => item.id);
        const selectedStandardIds = [];

        // Store a mapping from datasheetId to standard ID list
        const datasheetToStandardMap = {};

        const calibratedIDonly = certificates.filter((item) => item.calibratedby != null);
        const datasheetIds = calibratedIDonly.map((item) => item.id);


        // Step 1: Get standardRanges for each datasheet
        const promises = datasheetIds.map((id) =>
          axiosWithToken
            .get(`${url}datasheetStaticReadings?_where=(datasheetId,eq,${id})`)
            .then((response) => {
              const readings = response.data || [];
              const idsForThisSheet = [];

              readings.forEach((reading) => {
                if (reading.standardRanges) {
                  reading.standardRanges.split(",").forEach((sub) => {
                    const num = parseInt(sub.split(":")[0]);
                    if (!isNaN(num)) {
                      if (!selectedStandardIds.includes(num)) {
                        selectedStandardIds.push(num);
                      }
                      if (!idsForThisSheet.includes(num)) {
                        idsForThisSheet.push(num);
                      }
                    }
                  });
                }
              });

              datasheetToStandardMap[id] = idsForThisSheet;
            })
            .catch((err) => {
              console.error(
                `Error fetching datasheetStaticReadings for ID ${id}:`,
                err
              );
            })
        );

        await Promise.all(promises);

        // Step 2: Fetch all standard details
        let standards = [];
        if (selectedStandardIds.length) {
          const filteredStandardIds = selectedStandardIds.filter((id) =>
            selectedStdID.includes(id)
          );

          if (filteredStandardIds.length) {
            try {
              const standardsRes = await axiosWithToken.get(
                `${url}standards?_where=(id,in,${filteredStandardIds.join(
                  ","
                )})~and(status,eq,1)`
              );
              standards = standardsRes.data;
            } catch (err) {
              console.error("Error fetching standards:", err);
            }
          }
        }

        // Step 3: Map standard ID to standardName for fast lookup
        const standardMap = {};
        standards.forEach((std) => {
          standardMap[std.id] = std.standardName + ` (${std.stId})`;
        });

        // Step 4: Enrich each certificate with standardId and names
        const enrichedCertificates = certificates.map((cert) => {
          const stdIds = datasheetToStandardMap[cert.id] || [];
          const stdNames = stdIds.map((id) => standardMap[id]).filter(Boolean);

          return {
            ...cert,
            standardId: stdNames?.join(", ") || "",
          };
        });

        // Step 5: Final set
        const filteredCertificates = enrichedCertificates.filter(
          (cert) => cert.standardId && cert.standardId.length > 0
        );

        // Final set
        setData(filteredCertificates);
        setDownloadPdfloading(false);
      })
      .catch((error) => {
        setDownloadPdfloading(false);
        console.error("Error fetching certificatesView:", error);
      });
  };

  const fetchStandardsData = () => {
    let url = BASE_URL;
    let sqlQuery = {
      query: `SELECT disciplineId, standardName, validUpto, id, type, stId FROM standards WHERE id IN (SELECT MAX(id) FROM standards where status != -1 GROUP BY stId) AND status != -1`,
    };
    return axiosWithToken
      .post(url + "dynamic", sqlQuery)
      .then((res) => {

        const mappedStandards = res.data.map((item) => ({
          id: item.id,
          label: item.stId,
          standardName: item.standardName,
        }));
        setAllStandards(mappedStandards);
      })
      .catch((err) => {
        console.log("Fetch standard", err);
      });
  };

  const search = () => {
    setSearched(true);
    getData();
  };
  const clearSearch = () => {
    setSearched(false);
    setSelectedStdID([]);
    setFrom(null);
    setTo(null);
    setData([]);
  };

  useEffect(() => {
    fetchStandardsData();
  }, []);

  return (
    <ListWrap>
      <Grid
        container
        spacing={1}
        justifyContent={"start"}
        alignItems={"center"}
      >
        <Grid item xs={12} sm={12} md={6} lg={4} textAlign={"left"}>
          <Autocomplete
            size="small"
            className="Listinputcss"
            id="combo-box-demo"
            options={[
              { id: "selectAll", label: "Select All" },
              ...allStandards,
            ]} // Add "Select All" option
            disableCloseOnSelect
            multiple
            limitTags={3}
            value={allStandards.filter((option) =>
              selectedStdID.includes(option.id)
            )}
            onChange={(event, value) => {
              const selectedIds = value.map((e) => e.id);

              if (selectedIds.includes("selectAll")) {
                // If "Select All" is selected, select all options
                if (selectedStdID.length === allStandards.length) {
                  // If all are selected, remove "Select All"
                  setSelectedStdID([]);
                  setsearchSRNO([]);
                } else {
                  // Select all options
                  setSelectedStdID(allStandards.map((e) => e.id));
                  setsearchSRNO(allStandards.map((e) => e.label));
                }
              } else {
                const selectedLabels = value.map((e) => e.label);
                setSelectedStdID(selectedIds);
                setsearchSRNO(selectedLabels);
              }
              setSearched(false);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === "selectAll"
                      ? selectedStdID.length === allStandards.length
                      : selectedStdID.includes(option.id)
                  }
                />
                {option.standardName} ({option.label})
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Std Id." />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2} lg={2} textAlign={"left"}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              className="Listinputcss"
              label="From"
              value={from ? new Date(from) : null}
              inputFormat="dd/MM/yyyy"
              format="dd/MM/yyyy"
              onChange={(newValue) => {
                setFrom(newValue);
                setSearched(false);
              }}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6} sm={3} md={2} lg={2} textAlign={"left"}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              className="Listinputcss"
              label="To"
              value={to ? new Date(to) : null}
              inputFormat="dd/MM/yyyy"
              format="dd/MM/yyyy"
              onChange={(newValue) => {
                setTo(newValue);
                setSearched(false);
              }}
              renderInput={(params) => <TextField {...params} size="small" />}
              style={{ marginRight: "20px" }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={3} sm={3} md={3} lg={1} textAlign={"left"}>
          <Button
            variant="contained"
            size="small"
            className="Listinputcss"
            color={searched ? "error" : "primary"}
            sx={{ m: 0 }}
            disabled={!(selectedStdID.length > 0 && from && to)}
            onClick={() => {
              searched ? clearSearch() : search();
            }}
          >
            {searched ? <RestartAltIcon /> : <SearchIcon />}
          </Button>
        </Grid>
        {data.length > 0 && (
          <>
            <Grid item xs={4} sm={3} md={3} lg={2} textAlign={"left"}>
              <ExcelDownload
                finalData={data}
                notNeededColumn={notNeededColumn}
                headers={headers}
                secondTable={statusData}
              />
            </Grid>
          </>
        )}
      </Grid>

      <TableContainer>
        <div style={{ width: "100%", margin: "auto" }} ref={printComponentRef}>
          <Table
            stickyHeader
            aria-label="sticky table"
            size="small"
            style={{ borderCollapse: "collapse", marginTop: "5px" }}
          >
            <TableBody className="single-border-table">
              <TableRow>
                <TableCell>
                  <b>Sr.No.</b>
                </TableCell>
                <TableCell>
                  <b>Client Name</b>
                </TableCell>
                <TableCell>
                  <b>Certificate Number</b>
                </TableCell>
                <TableCell>
                  <b>Instrument Name</b>
                </TableCell>
                <TableCell>
                  <b>DUCID</b>
                </TableCell>
                <TableCell>
                  <b>Serial No</b>
                </TableCell>
                <TableCell>
                  <b>Date of Calibration</b>
                </TableCell>
                <TableCell>
                  <b>Master Equipment Used (StdID)</b>
                </TableCell>
              </TableRow>
              {data?.length > 0 ? (
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <TableRow
                        id="pagbreaksborder"
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index}
                        style={{ pageBreakInside: "avoid" }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.companyName}</TableCell>
                        <TableCell>{row.certificateNumber}</TableCell>
                        <TableCell>{row.instrumentName}</TableCell>
                        <TableCell>{row.DUCID}</TableCell>
                        <TableCell>{row.serialNumber}</TableCell>
                        <TableCell>{row.calibrationDate}</TableCell>
                        <TableCell>{row.standardId}</TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <h6>
                      <b>Data Not Found</b>
                    </h6>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {downloadPdfloading && (
        <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Master"} />
      )}
    </ListWrap>
  );
}
